import React, { ReactNode } from "react";
import { DailyIcon } from "@/features/daily/components/DailyIcon";
import type { JourneyCardVm } from "@/features/daily/components/funnel/funnel-view-model";

type Props = {
    title: string;
    pill: string;
    card: JourneyCardVm | null;
    emptyMessage: string;
    children?: ReactNode;
};

/**
 * FR-020: one journey, rendered on its own. Conversion up to funding is a commercial
 * question and execution after funding is an operational one; putting them on the
 * same ladder is what made a payer who never paid look like a platform failure.
 */
export const JourneyCard: React.FC<Props> = ({ title, pill, card, emptyMessage, children }) => (
    <section className="daily-panel" aria-label={title} data-tone={card === null ? undefined : card.tone}>
        <div className="daily-panel__head">
            <h2>{title}</h2>
            <span className="daily-pill">{pill}</span>
        </div>
        {card === null ? (
            <p className="daily-small daily-muted">{emptyMessage}</p>
        ) : (
            <>
                <div className="daily-flow">
                    <div className="daily-flow__stage">
                        <span>{card.from.label}</span>
                        <strong>{card.from.valueLabel}</strong>
                        <small>{card.from.note}</small>
                    </div>
                    <DailyIcon name="arrow_forward" />
                    <div className="daily-flow__stage">
                        <span>{card.to.label}</span>
                        <strong>{card.to.valueLabel}</strong>
                        <small>{card.to.note}</small>
                    </div>
                </div>
                {card.progressPct === null ? null : (
                    <div className="daily-progress" aria-hidden="true">
                        <span style={{ width: `${Math.min(100, Math.max(0, card.progressPct))}%` }} />
                    </div>
                )}
                <p className="daily-small daily-muted">{card.footnote}</p>
                {card.reasons.length === 0 ? null : (
                    <ul className="daily-reasons">
                        {card.reasons.map((reason) => <li key={reason}>{reason}</li>)}
                    </ul>
                )}
            </>
        )}
        {children}
    </section>
);
